import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { Tea, Teaware, ProductSet } from '../model/types'

// ============================================================
// Типы параметров
// ============================================================

export interface ProductFilters {
  /** UUID категории */
  category?: string
  /** UUID подкатегории */
  subcategory?: string
  /** Сортировка */
  sort?: 'popular' | 'price-asc' | 'price-desc' | 'newest'
  /** Поисковый запрос */
  search?: string
  /** Минимальная цена */
  priceMin?: number
  /** Максимальная цена */
  priceMax?: number
}

export interface PaginationParams {
  page?: number
  limit?: number
}

export type GetTeaParams = ProductFilters & PaginationParams & {
  featured?: boolean
}

export type GetTeawareParams = ProductFilters & PaginationParams

// ============================================================
// Ответы API
// ============================================================

export interface ProductsResponse<T> {
  data: T[]
  meta?: {
    pagination: {
      page: number
      limit: number
      total: number
      totalPages: number
      hasNext: boolean
      hasPrev: boolean
    }
  }
}

export interface ProductResponse<T> {
  data: T
}

// ============================================================
// Утилиты
// ============================================================

/**
 * Строит URLSearchParams из параметров фильтрации
 */
function buildSearchParams(
  params: ProductFilters & PaginationParams & { featured?: boolean }
): URLSearchParams {
  const searchParams = new URLSearchParams()

  // Категория (UUID)
  if (params.category?.trim()) {
    searchParams.append('categoryId', params.category.trim())
  }
  
  // Подкатегория (UUID)
  if (params.subcategory?.trim()) {
    searchParams.append('subcategoryId', params.subcategory.trim())
  }
  
  // Остальные параметры
  if (params.sort) searchParams.append('sort', params.sort)
  if (params.search) searchParams.append('search', params.search)
  if (params.featured !== undefined) searchParams.append('featured', String(params.featured))
  if (params.priceMin) searchParams.append('price_min', params.priceMin.toString())
  if (params.priceMax) searchParams.append('price_max', params.priceMax.toString())
  if (params.page) searchParams.append('page', params.page.toString())
  if (params.limit) searchParams.append('limit', params.limit.toString())

  return searchParams
}

/**
 * Строит URL endpoint с query параметрами
 */
function buildEndpoint(base: string, searchParams: URLSearchParams): string {
  const queryString = searchParams.toString()
  return queryString ? `${base}?${queryString}` : base
}

// ============================================================
// Хуки для чая
// ============================================================

/**
 * Получить список чая с фильтрацией и пагинацией
 */
export function useTea(params: GetTeaParams = {}) {
  return useQuery({
    queryKey: ['tea', params],
    queryFn: () => {
      const searchParams = buildSearchParams(params)
      return apiClient<ProductsResponse<Tea>>(buildEndpoint('/api/public/tea', searchParams))
    },
  })
}

/**
 * Получить чай по ID
 */
export function useTeaById(id: string) {
  return useQuery({
    queryKey: ['tea', id],
    queryFn: () => apiClient<ProductResponse<Tea>>(`/api/public/tea/${id}`),
    enabled: !!id,
  })
}

/**
 * Получить список чая с бесконечной прокруткой
 */
export function useInfiniteTea(
  params: Omit<GetTeaParams, 'page' | 'limit'> = {},
  limit = 20
) {
  return useInfiniteQuery({
    queryKey: ['tea', 'infinite', params],
    queryFn: ({ pageParam = 1 }) => {
      const searchParams = buildSearchParams({ ...params, page: pageParam, limit })
      return apiClient<ProductsResponse<Tea>>(buildEndpoint('/api/public/tea', searchParams))
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.meta?.pagination
      return pagination?.hasNext ? pagination.page + 1 : undefined
    },
    initialPageParam: 1,
  })
}

// ============================================================
// Расширенный поиск
// ============================================================

export interface ExtendedSearchResult {
  byName: Tea[]
  byStyle: Tea[]
  categories: Array<{
    id: string
    name: string
    slug: string
    type: 'category' | 'subcategory'
    teaCount: number
  }>
}

export interface ExtendedSearchResponse {
  data: ExtendedSearchResult
}

/**
 * Расширенный поиск с группировкой результатов
 * Возвращает чаи по: названию, стилю (категории/подкатегории)
 */
export function useExtendedTeaSearch(query: string, limit = 5) {
  return useQuery({
    queryKey: ['tea-search', query, limit],
    queryFn: () => {
      const params = new URLSearchParams()
      params.append('q', query)
      params.append('limit', limit.toString())
      return apiClient<ExtendedSearchResponse>(`/api/public/tea/search?${params.toString()}`)
    },
    enabled: query.trim().length > 0,
    staleTime: 30 * 1000, // 30 секунд
  })
}

// ============================================================
// Хуки для посуды
// ============================================================

/**
 * Получить список посуды с фильтрацией и пагинацией
 */
export function useTeaware(params: GetTeawareParams = {}) {
  return useQuery({
    queryKey: ['teaware', params],
    queryFn: () => {
      const searchParams = buildSearchParams(params)
      return apiClient<ProductsResponse<Teaware>>(buildEndpoint('/api/public/teaware', searchParams))
    },
  })
}

/**
 * Получить посуду по ID
 */
export function useTeawareById(id: string) {
  return useQuery({
    queryKey: ['teaware', id],
    queryFn: () => apiClient<ProductResponse<Teaware>>(`/api/public/teaware/${id}`),
    enabled: !!id,
  })
}

/**
 * Получить список посуды с бесконечной прокруткой
 */
export function useInfiniteTeaware(
  params: Omit<GetTeawareParams, 'page' | 'limit'> = {},
  limit = 20
) {
  return useInfiniteQuery({
    queryKey: ['teaware', 'infinite', params],
    queryFn: ({ pageParam = 1 }) => {
      const searchParams = buildSearchParams({ ...params, page: pageParam, limit })
      return apiClient<ProductsResponse<Teaware>>(buildEndpoint('/api/public/teaware', searchParams))
    },
    getNextPageParam: (lastPage) => {
      const pagination = lastPage.meta?.pagination
      return pagination?.hasNext ? pagination.page + 1 : undefined
    },
    initialPageParam: 1,
  })
}

// ============================================================
// Хуки для наборов (product_set)
// ============================================================

export interface GetProductSetsParams {
  sort?: 'popular' | 'price-asc' | 'price-desc' | 'newest'
  search?: string
  /** Только доступные к покупке (в наличии + все компоненты доступны). */
  purchasableOnly?: boolean
}

/**
 * Backend отдаёт наборы как { data: ProductSet[] } (без пагинации/меты).
 * Для единообразия оборачиваем в ProductsResponse.
 */
interface ProductSetsResponse {
  data: ProductSet[]
}

/**
 * Получить список наборов с сортировкой.
 * Эндпоинт: GET /api/public/product-sets
 */
export function useProductSets(params: GetProductSetsParams = {}) {
  return useQuery({
    queryKey: ['product-sets', params],
    queryFn: () => {
      const sp = new URLSearchParams()
      if (params.sort) sp.append('sort', params.sort)
      if (params.search) sp.append('search', params.search)
      if (params.purchasableOnly !== undefined) sp.append('purchasableOnly', String(params.purchasableOnly))
      return apiClient<ProductSetsResponse>(buildEndpoint('/api/public/product-sets', sp))
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Получить набор по ID или slug.
 * Эндпоинт: GET /api/public/product-sets/:idOrSlug
 */
export function useProductSetById(idOrSlug: string) {
  return useQuery({
    queryKey: ['product-set', idOrSlug],
    queryFn: () => apiClient<ProductResponse<ProductSet>>(`/api/public/product-sets/${idOrSlug}`),
    enabled: !!idOrSlug,
  })
}
